import type { ItemFieldRow } from './item-field-reader';
import { readInventoryTemplateId } from './item-field-reader';
import type { InventoryReadContext } from './inventory-read-context';
import { createInventoryReadContext } from './inventory-read-context';
import { phpAdd, phpSubtract, phpMultiply, phpDivide, phpLess, phpMin, phpMax,
    phpFloatToInteger, phpJsonInteger } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';

function arrayLike(value: unknown): value is ItemFieldRow { return value !== null && typeof value === 'object'; }
const has = (value: unknown, key: string): boolean => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key);
const own = (value: unknown, key: string): unknown => has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
const ids = {
    invigorated: '82788F70-EBEF-44C4-80E7-6622AD20DD60', fresh: 'FF30B76E-785D-2CAF-FC66-C131D0548808',
    barely_wounded: '658483AA-34DE-74E5-C974-E3E202C31CC7', wounded: 'C97076EF-FED9-19B4-D109-C22F9BC740E8',
    bloodied: '44B8792E-632E-2651-C666-67DFD181E92A', well_bloodied: '298BE1DD-2917-476E-D79C-92CF8C598926',
    on_deaths_door: 'D5DE14C4-3194-87BC-D767-6D45BDD90552', dying: '8AE129AF-52D8-CCE8-706A-85767DB52740',
} as const;
export { ids as VITALITY_STATUS_IDS };
export function calculateVitalityStatus(current: PhpArithmetic, maximum: PhpArithmetic, temp: PhpArithmetic): string {
    if (!phpLess(0n, maximum)) return ids.dying;
    const ratio = Number(phpDivide(current, maximum));
    if (ratio >= 1) return phpLess(0n, temp) ? ids.invigorated : ids.fresh;
    if (ratio > 0.75) return ids.barely_wounded;
    if (ratio > 0.50) return ids.wounded;
    if (ratio > 0.25) return ids.bloodied;
    if (ratio > 0.10) return ids.well_bloodied;
    return ratio > 0 ? ids.on_deaths_door : ids.dying;
}

/** ft_hydrate_vitality over JSON-decoded PHP array values. Existing keys, including
 * explicit nulls, win. Integer arithmetic stays exact until PHP promotes to double.
 */
export function hydrateVitality(input: ItemFieldRow): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Vitality must be a PHP array');
    if (own(input, 'v_status') != null) return clone(input) as ItemFieldRow;
    const baseMax = phpJsonInteger(own(input, 'base_v_max') ?? own(input, 'v_max') ?? 0);
    const current = has(input, 'base_v_current') ? phpJsonInteger(own(input, 'base_v_current'))
        : has(input, 'v_current') ? phpJsonInteger(own(input, 'v_current')) : baseMax;
    const temp = phpJsonInteger(own(input, 'base_v_temp') ?? own(input, 'v_temp') ?? 0);
    const maxTemp = phpJsonInteger(own(input, 'base_v_max_temp') ?? own(input, 'v_max_temp') ?? 0);
    const effectiveMax = phpMax(1n, phpAdd(baseMax, maxTemp));
    const cur = phpMax(0n, phpMin(current, effectiveMax));
    const total = phpAdd(effectiveMax, temp);
    const percent = (value: PhpArithmetic): number => phpLess(0n, total)
        ? Number(phpFloatToInteger(Math.floor(Number(phpMultiply(phpDivide(value, total), 100n))))) : 0;
    const out: Record<string, unknown> = Object.fromEntries(Object.entries(input));
    const append = (fields: Record<string, unknown>): void => {
        for (const [key, value] of Object.entries(fields)) if (!has(out, key)) Object.defineProperty(out, key, { value, enumerable: true, writable: true, configurable: true });
    };
    append({ v_current: Number(cur), v_max: Number(baseMax), effective_v_max: Number(effectiveMax),
        v_status: calculateVitalityStatus(cur, effectiveMax, temp), v_percent: percent(cur), v_bar_v_percent: percent(cur), v_bar_total: Number(total) });
    const lost = phpSubtract(effectiveMax, cur);
    if (lost !== 0n) append({ v_lost: Number(lost) });
    if (temp !== 0n) append({ v_temp: Number(temp), v_temp_percent: percent(temp), v_bar_v_temp_percent: percent(temp) });
    if (maxTemp !== 0n) append({ v_max_temp: Number(maxTemp), v_max_temp_percent: percent(maxTemp) });
    return clone(out) as ItemFieldRow;
}

export interface VitalityReaderDependencies {
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    inventoryContext?: InventoryReadContext;
    legacyRead?: (path: 'computed.v_arr' | 'v_arr') => void;
}
/** Complete vitality_of selection, including the shared instance bridge and
 * legacy raw-bucket fallbacks. Supplied full results must never be fabricated.
 */
export function createVitalityReader(deps: VitalityReaderDependencies): (row: ItemFieldRow | null) => ItemFieldRow | null {
    const context = deps.inventoryContext ?? createInventoryReadContext(deps.resolveInventoryInstance);
    function read(row: ItemFieldRow | null): ItemFieldRow | null {
        if (row === null) return null;
        if (!arrayLike(row)) throw new TypeError('Vitality row must be a PHP array or null');
        const v = own(own(row, 'computed'), 'vitality');
        const id = readInventoryTemplateId(row);
        if (id !== null) {
            const catalog = own(row, 'catalog');
            const definition = own(catalog, id) ?? own(catalog, id.toUpperCase()) ?? own(catalog, id.toLowerCase());
            const defaults = own(own(definition, 'computed'), 'vitality');
            if (arrayLike(defaults)) {
                return hydrateVitality(arrayLike(v) ? Object.fromEntries([...Object.entries(defaults), ...Object.entries(v)]) : defaults);
            }
            if (!arrayLike(v) || !has(v, 'base_v_max') || !has(v, 'base_v_current')) {
                const bridged = context.read(row, read);
                if (arrayLike(bridged)) return bridged;
            }
        }
        if (arrayLike(v)) return hydrateVitality(v);
        const legacy = own(own(row, 'computed'), 'v_arr');
        if (arrayLike(legacy)) { deps.legacyRead?.('computed.v_arr'); return clone(legacy) as ItemFieldRow; }
        const rootLegacy = own(row, 'v_arr');
        if (arrayLike(rootLegacy)) { deps.legacyRead?.('v_arr'); return clone(rootLegacy) as ItemFieldRow; }
        return null;
    }
    return read;
}

/** ft_inventory_vitality_source_row: positive authored maximum preserves current
 * (including zero). Otherwise a positive template maximum initializes both bases.
 */
export function selectInventoryVitalitySource(row: ItemFieldRow, template: ItemFieldRow,
    readVitality: (row: ItemFieldRow) => ItemFieldRow | null): ItemFieldRow {
    const positiveNumeric = (value: unknown): boolean => (typeof value === 'number'
        || (typeof value === 'string' && /^[\t\n\r\v\f ]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?[\t\n\r\v\f ]*$/i.test(value))) && Number(value) > 0;
    if (positiveNumeric(own(row, 'v_max'))) return clone(row) as ItemFieldRow;
    const vitality = readVitality(template);
    const maximum = own(vitality, 'base_v_max') ?? own(vitality, 'v_max');
    if (!positiveNumeric(maximum)) return clone(row) as ItemFieldRow;
    return clone(Object.fromEntries([...Object.entries(row), ['v_max', maximum], ['v_current', maximum]])) as ItemFieldRow;
}
